import React, { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import theme, { inputClasses, cardClasses, textClasses, buttonClasses } from '../../static/styles/theme';
import { showToast } from '../../services/functions';
import { resendOtpFor2FA, verifyOtpFor2FA } from '../../redux/actions';
import axios from '../../services/Api';
import history from '../../services/history';
import { encryptTheData } from '../../services/Api';

const TwoFactorAuth = ({ open2FaForm, setOpen2FaForm, handleBackBtnClick, getValues, dispatch }) => {
  const { control, formState: { errors } } = useForm();
  const [otpLoading, setOtpLoading] = useState(false);
  const [otp2FA, setOtp2FA] = useState(['', '', '', '', '', '']);
  const [seconds, setSeconds] = useState(30); // Initial value for 30 seconds
  const timerRef = useRef(null);
  const inputRefs = useRef([...Array(6)].map(() => React.createRef()));

  useEffect(() => {
    if (seconds > 0) {
      timerRef.current = setInterval(() => {
        setSeconds((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(timerRef.current);
  }, [seconds]);

  useEffect(() => {
    return () => clearInterval(timerRef.current);
  }, []);

  const handleSubmitOTP = () => {
    setOtpLoading(true);
    const data = getValues();
    const payload = {
      type: 'twoFactorAuth',
      otp: otp2FA.join(''),
      projectId: 'WMS',
      loginType: 'web',
    };
    if (data.mobile != '') {
      payload.mobile = data?.mobile;
    }
    payload.email = data?.email;

    setOtpLoading(false);
    let encryptedData = { payloadData: encryptTheData(payload) };
    dispatch(
      verifyOtpFor2FA(
        encryptedData,
        (response = {}) => {
          showToast(response.message, !response.success);
          setOtpLoading(false);
          console.log("respss", response)
          if (response?.statusCode == 204) {
            setOtp2FA(['', '', '', '', '', '']);
            clearInterval(timerRef.current);
            handleBackBtnClick();
          }
          if (response && response.success) {
            // Complete login process after successful 2FA
            localStorage.setItem('token', JSON.stringify(response.token));
            localStorage.setItem('user', JSON.stringify(response.user));
            axios.defaults.headers.common['authorization'] = response.token;
            dispatch({ type: 'FETCH_SUCCESS' });
            history.push(response.initUrl || '/dashboard');
          }
        },
      )
    );
  };

  const handleResendClick = () => {
    setOtpLoading(true);
    const data = getValues();
    const payload = {
      type: 'twoFactorAuth',
      projectId: 'WMS',
      loginType: 'web',
    };

    if (data.mobile != '') {
      payload.mobile = data?.mobile;
    }
    payload.email = data?.email;
    setOtpLoading(false);
    setSeconds(30);
    setOtp2FA(['', '', '', '', '', '']);
    let encryptedData = { payloadData: encryptTheData(payload) };
    dispatch(
      resendOtpFor2FA(
        encryptedData,
        (response = {}) => {
          showToast(response.message, !response.success);
          setOtpLoading(false);
          console.log("respss", response)
          if (response?.statusCode == 204) {
            setOtp2FA(['', '', '', '', '', '']);
            clearInterval(timerRef.current);
            handleBackBtnClick();
          }
        },
      )
    );
  };

  const onPressingEnterFunction = (e) => {
    if (e.key === 'Enter' && otp2FA.join('').length === 6 && !otpLoading) {
      handleSubmitOTP();
    }
  };

  const handleOtpChange = (index, value, onChange) => {
    // Allow only numeric characters and limit to 1 digit per box
    const numericValue = value?.replace(/[^0-9]/g, '').slice(0, 1);
    const newOtp = [...otp2FA];
    newOtp[index] = numericValue;
    setOtp2FA(newOtp);
    onChange(newOtp.join(''));

    // Move focus to the next input if a digit is entered
    if (numericValue && index < 5) {
      inputRefs.current[index + 1].current.focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === 'Backspace' && !otp2FA[index] && index > 0) {
      const newOtp = [...otp2FA];
      newOtp[index] = '';
      setOtp2FA(newOtp);
      inputRefs.current[index - 1].current.focus();
    }
  };

  return (
    <Card
      sx={{
        ml: { xs: '10px', sm: 2, lg: 13 },
        mr: { xs: '10px', sm: 2, lg: 13 },
        minWidth: '300px',
        maxWidth: '530px',
        height: 'auto',
        width: '90%',
        zIndex: 2000,
        position: 'absolute',
        top: { xs: '30%', sm: 'unset' },
        left: { xs: '50%', sm: '50%', md: 'unset' },
        transform: { xs: 'translateX(-50%)', sm: 'unset' },
        ...cardClasses.basic,
      }}
      className="mui-custom-card"
    >
      <CardContent>
        <Box
          mb={2}
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <IconButton
            sx={{ marginRight: 'auto', color: 'black' }} // Black arrow
            onClick={() => {
              setOtp2FA(['', '', '', '', '', '']);
              clearInterval(timerRef.current);
              handleBackBtnClick(); // Return to main login field
            }}
            data-testid="back-btn-2fa"
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography
            sx={{
              fontSize: '17px',
              fontWeight: '700',
              width: '100%',
              textAlign: 'center',
            }}
          >
            Two Factor Authentication
          </Typography>
        </Box>
        <Grid container my={3}>
          <Typography sx={{ textAlign: 'center' }}>
            {open2FaForm?.displayMessage || 'Enter the OTP sent to your email or mobile'}
          </Typography>
          <Grid item xs={12} my={2} sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
            {[...Array(6)].map((_, index) => (
              <Controller
                key={index}
                name={`otp${index}`}
                control={control}
                rules={{ required: true, validate: (value) => value.length === 1 || !value }}
                render={({ field }) => (
                  <TextField
                    inputRef={inputRefs.current[index]}
                    variant="outlined"
                    sx={{
                      ...inputClasses.textField,
                      width: '40px',
                      textAlign: 'center',
                      borderRadius: '1px',
                    }}
                    size="small"
                    type="text"
                    error={!!errors[`otp${index}`]}
                    helperText={errors[`otp${index}`] ? 'Invalid digit' : ''}
                    value={otp2FA[index]}
                    onChange={(e) => handleOtpChange(index, e.target.value, field.onChange)}
                    onKeyDown={(e) => handleKeyDown(e, index)}
                    inputProps={{ maxLength: 1 }}
                  />
                )}
              />
            ))}
            {errors.otp && (
              <Typography color="error" sx={{ mt: 1, textAlign: 'center' }}>
                Please enter a valid 6-digit OTP
              </Typography>
            )}
          </Grid>
          <Grid
            item
            xs={12}
            mb={1}
            display="flex"
            justifyContent="center"
            alignItems="center"
          >
            {seconds === 0 ? (
              <Box width="100%" display="flex" alignItems="center" justifyContent="space-between">
                <Typography
                  sx={{
                    ...textClasses.boldText,
                    textTransform: 'none',
                  }}
                >
                  Didn't get the OTP?
                </Typography>
                <Button
                  variant="text"
                  disabled={otpLoading}
                  sx={{
                    textTransform: 'none',
                    color: 'var(--primary-color)',
                  }}
                  onClick={handleResendClick}
                  data-testid="resend-otp-btn"
                >
                  Resend OTP
                </Button>
              </Box>
            ) : (
              <Typography
                sx={{
                  ...textClasses.boldText,
                  textTransform: 'none',
                  textAlign: 'center',
                }}
              >
                Resend OTP in {seconds} seconds
              </Typography>
            )}
          </Grid>
          <Grid item xs={12} mt={1} container justifyContent="center">
            <Button
              variant="contained"
              size="large"
              data-testid="submit-btn"
              onClick={handleSubmitOTP}
              sx={{
                ...buttonClasses.lynkitOrangeEmpty,
                m: 1,
                fontSize: '.7rem',
                width: '300px',
                '&.Mui-disabled': {
                  backgroundColor: '#d3d3d3', // Light gray color
                  color: '#000000', // Optional: Adjust text color for readability
                },
              }}
              disabled={otpLoading || otp2FA.join('').length !== 6}
            >
              {otpLoading ? <CircularProgress size={20} /> : 'Submit'}
            </Button>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default TwoFactorAuth;